import { Controller } from "@hotwired/stimulus"
import { patch, post } from "@rails/request.js"

// Connects to data-controller="engage--nps"
// Handles NPS score selection, comment reveal, submit + dismiss.
export default class extends Controller {
  static targets = [
    "form",
    "thankyou",
    "scoreBtn",
    "commentField",
    "comment",
    "submitBtn",
    "modal",
  ]
  static values = {
    flowId: String,
    surveyPeriod: String,
  }

  #selectedScore = null
  #abortController = null
  #removeTimer = null

  connect() {
    this.#abortController = new AbortController()
  }

  disconnect() {
    this.#abortController?.abort()
    if (this.#removeTimer) clearTimeout(this.#removeTimer)
  }

  async dismiss() {
    await this.#patch(`/api/v1/engage/flow_states/${this.flowIdValue}/dismiss`)
    this.element.remove()
  }

  async remindLater() {
    // Snooze: dismiss server-side (collection cool-off will re-surface later)
    await this.#patch(
      `/api/v1/engage/flow_states/${this.flowIdValue}/dismiss`,
      {
        snooze: true,
      },
    )
    this.element.remove()
  }

  selectScore(event) {
    const score = parseInt(event.currentTarget.dataset.score, 10)
    if (isNaN(score)) return

    this.#selectedScore = score

    // Visual selection
    this.scoreBtnTargets.forEach((btn) => {
      const btnScore = parseInt(btn.dataset.score, 10)
      btn.setAttribute("aria-checked", btnScore === score ? "true" : "false")
      if (btnScore === score) {
        btn.classList.add(
          "scale-110",
          "ring-2",
          "ring-gray-800",
          "ring-offset-1",
        )
      } else {
        btn.classList.remove(
          "scale-110",
          "ring-2",
          "ring-gray-800",
          "ring-offset-1",
        )
      }
    })

    // Show comment field + enable submit
    if (this.hasCommentFieldTarget) this.commentFieldTarget.style.display = ""
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false
  }

  async submit() {
    if (this.#selectedScore === null) return
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true
    if (this.hasSubmitBtnTarget)
      this.submitBtnTarget.textContent = "Submitting…"

    const comment = this.hasCommentTarget ? this.commentTarget.value : ""

    try {
      const response = await post("/api/v1/engage/nps/response", {
        body: {
          flow_id: this.flowIdValue,
          score: this.#selectedScore,
          comment: comment || null,
          survey_period: this.surveyPeriodValue || null,
        },
        credentials: "include",
        responseKind: "json",
      })

      if (!response.ok) {
        throw new Error(`API error ${response.statusCode}`)
      }

      // Show thank-you, hide form
      if (this.hasFormTarget) this.formTarget.style.display = "none"
      if (this.hasThankyouTarget) this.thankyouTarget.style.display = ""

      this.#removeTimer = setTimeout(() => this.element.remove(), 2000)
    } catch {
      if (this.hasSubmitBtnTarget) {
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = "Submit"
      }
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────

  async #patch(url, body = null) {
    try {
      const options = { credentials: "include", responseKind: "json" }
      if (this.#abortController) options.signal = this.#abortController.signal
      if (body) options.body = body
      const response = await patch(url, options)
      if (!response.ok)
        console.error(`[Engage] API error ${response.statusCode}`, url)
      return response
    } catch (e) {
      if (e.name !== "AbortError") throw e
    }
  }
}
